import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { getUser } from "@/lib/api";
import { MockupThc } from "@/components/mockups/MockupThc";
import { MockupConsulting } from "@/components/mockups/MockupConsulting";
import { SINGLE_PAGES, SinglePageMockup } from "@/components/mockups/SinglePageMockup";

const SIZES = { wide: [1200, 627], square: [1080, 1080] };

const ExportBlock = ({ id, title, Mockup }) => {
  const refs = { wide: useRef(null), square: useRef(null) };
  const [busy, setBusy] = useState("");

  async function download(format) {
    const [w, h] = SIZES[format];
    setBusy(format);
    try {
      const url = await toPng(refs[format].current, { width: w, height: h, pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.href = url;
      a.download = `optimizarrhh-${id}-${w}x${h}.png`;
      a.click();
    } catch (e) {
      toast.error("No se pudo exportar la imagen");
    } finally {
      setBusy("");
    }
  }

  return (
    <section className="mb-16" data-testid={`mockup-block-${id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-display text-xl font-bold text-slate-900">{title}</h2>
        <div className="flex gap-2">
          {Object.keys(SIZES).map((f) => (
            <button key={f} onClick={() => download(f)} disabled={!!busy} className="btn-primary disabled:opacity-60" data-testid={`mockup-${id}-download-${f}`}>
              <Download className="w-4 h-4" />
              {busy === f ? "Exportando…" : `Descargar PNG ${SIZES[f][0]}×${SIZES[f][1]}`}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-6">
        {Object.keys(SIZES).map((f) => (
          <div key={f} className="rounded-xl border border-slate-200 bg-white p-3 overflow-x-auto" data-testid={`mockup-${id}-${f}`}>
            <div className="text-xs text-slate-500 mb-2">{SIZES[f][0]} × {SIZES[f][1]} px (se exporta a doble resolución)</div>
            <Mockup format={f} innerRef={refs[f]} />
          </div>
        ))}
      </div>
    </section>
  );
};

const SingleCard = ({ id, label, index }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  async function download() {
    setBusy(true);
    try {
      const url = await toPng(ref.current, { width: 1080, height: 1350, pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.href = url;
      a.download = `optimizarrhh-pagina-${id}-1080x1350.png`;
      a.click();
    } catch (e) {
      toast.error("No se pudo exportar la imagen");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`single-mockup-${id}`}>
      <div className="overflow-hidden" style={{ width: 270, height: 337.5 }}>
        <div style={{ transform: "scale(0.25)", transformOrigin: "top left" }}>
          <SinglePageMockup id={id} index={index} innerRef={ref} />
        </div>
      </div>
      <div className="mt-2 text-sm font-medium text-slate-800">{label}</div>
      <button onClick={download} disabled={busy} className="btn-ghost mt-2 w-full justify-center" data-testid={`single-mockup-${id}-download`}>
        <Download className="w-4 h-4" />{busy ? "Exportando…" : "Descargar PNG"}
      </button>
    </div>
  );
};

const SinglePages = () => {
  let i = 0;
  return (
    <section className="mb-16" data-testid="single-pages-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h2 className="font-display text-xl font-bold text-slate-900">Páginas individuales · 1080 × 1350 px</h2>
        <a href="/mockups/export/optimizarrhh-paginas-individuales.zip" className="btn-primary" data-testid="single-pages-zip">
          <Download className="w-4 h-4" />Descargar todas (ZIP)
        </a>
      </div>
      <p className="text-slate-500 text-sm mb-6">Se exportan a doble resolución (2160 × 2700 px).</p>
      {SINGLE_PAGES.map((g) => (
        <div key={g.group} className="mb-8">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-[#085041] mb-3">{g.group}</h3>
          <div className="flex flex-wrap gap-4">
            {g.items.map(([id, label]) => <SingleCard key={id} id={id} label={label} index={i++} />)}
          </div>
        </div>
      ))}
    </section>
  );
};

export default function AdminMockups() {
  const navigate = useNavigate();
  const user = getUser();
  useEffect(() => {
    if (!user?.is_admin) navigate("/dashboard");
  }, [navigate, user?.is_admin]);
  if (!user?.is_admin) return null;
  return (
    <div className="min-h-screen bg-slate-50 px-6 py-10" data-testid="admin-mockups-page">
      <div className="max-w-[1260px] mx-auto">
        <h1 className="font-display text-3xl font-bold text-slate-900 mb-2">Mockups para LinkedIn · Destacados</h1>
        <p className="text-slate-500 mb-10 text-sm">Páginas reales de los PDF generados por la web para la empresa de ejemplo «OptimizaRRHH».</p>
        <ExportBlock id="talent-health-check" title="Mockup 1 · Diagnóstico rápido" Mockup={MockupThc} />
        <ExportBlock id="consultoria-premium" title="Mockup 2 · Consultoría premium" Mockup={MockupConsulting} />
        <SinglePages />
      </div>
    </div>
  );
}
